import type { DrawMode, LatLngTuple } from "../core/options";
import type { DrawGeometry } from "../core/store";
import { areaText, lengthText, lineLengthMeters, ringAreaSquareMeters, ringPerimeterMeters } from "../geo/geodesy";
import { lineWkt, pointWkt, polygonWkt } from "../geo/wkt";
import type { Labels } from "../i18n/labels";
import { validate } from "./Validation";

export type MeasureMode = "distance" | "area";

export function measureGeometry(mode: DrawMode | null, points: LatLngTuple[], labels: Labels): DrawGeometry {
  const validation = validate(mode, points, labels);
  const lengthMeters = mode === "polyline" ? lineLengthMeters(points) : mode === "polygon" ? ringPerimeterMeters(points) : 0;
  const perimeterMeters = mode === "polygon" ? ringPerimeterMeters(points) : 0;
  const areaSquareMeters = mode === "polygon" ? ringAreaSquareMeters(points) : 0;
  let wkt: string | null = null;

  if (validation.valid) {
    if (mode === "point") wkt = pointWkt(points[0]);
    else if (mode === "polyline") wkt = lineWkt(points);
    else if (mode === "polygon") wkt = polygonWkt(points);
  }

  return {
    mode,
    wkt,
    points: points.map(a => [a[0], a[1]]),
    lengthMeters,
    areaSquareMeters,
    perimeterMeters,
    valid: validation.valid,
    reason: validation.reason
  };
}

export function measureSummary(mode: MeasureMode, points: LatLngTuple[], labels: Labels): string {
  if (mode === "distance") return `${labels.drawTotal}: ${lengthText(lineLengthMeters(points))}`;

  return `${labels.drawArea}: ${areaText(ringAreaSquareMeters(points))} · ${labels.drawPerimeter}: ${lengthText(ringPerimeterMeters(points))}`;
}

export function drawSummary(geometry: DrawGeometry, labels: Labels): string | null {
  if (geometry.mode === null || geometry.mode === "point" || geometry.points.length === 0) return null;

  const length = `${geometry.mode === "polygon" ? labels.drawPerimeter : labels.drawTotal}: ${lengthText(geometry.mode === "polygon" ? geometry.perimeterMeters : geometry.lengthMeters)}`;

  return geometry.mode === "polygon" ? `${labels.drawArea}: ${areaText(geometry.areaSquareMeters)} · ${length}` : length;
}
